import { GridFilterItem } from '@mui/x-data-grid-pro'

const DATE_COMPARE_OPERATORS = new Set(['before', 'after', 'onOrAfter', 'onOrBefore', 'between'])

const ymdPattern = /^(\d{4})-(\d{2})-(\d{2})$/

/** Local calendar day as midnight timestamp. `YYYY-MM-DD` stays on that calendar day. */
export const parseInventoryV3CalendarDay = (value: unknown): number | null => {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null
    return new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime()
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return parseInventoryV3CalendarDay(new Date(value))
  }
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed) return null
  const ymd = ymdPattern.exec(trimmed)
  if (ymd) {
    const year = Number(ymd[1])
    const month = Number(ymd[2])
    const day = Number(ymd[3])
    const date = new Date(year, month - 1, day)
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null
    return date.getTime()
  }
  const parsed = new Date(trimmed)
  if (Number.isNaN(parsed.getTime())) return null
  return parseInventoryV3CalendarDay(parsed)
}

/** Grid cell value for a date column. Keeps the stored instant so sorting stays chronological. */
export const inventoryV3DateCellValue = (value: unknown): Date | null => {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed) return null
  const parsed = new Date(trimmed)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

/** Persist a picked or edited date back onto the row as an ISO string. */
export const inventoryV3DateStoredValue = (value: unknown): string | null => {
  if (value == null || value === '') return null
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.toISOString()
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed) return null
  const ymd = ymdPattern.exec(trimmed)
  if (ymd) {
    const date = new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]), 12, 0, 0, 0)
    return date.toISOString()
  }
  const parsed = new Date(trimmed)
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString()
}

export const inventoryV3DateRangeValue = (value: unknown): [string, string] => {
  if (!Array.isArray(value)) return ['', '']
  return [value[0] == null ? '' : String(value[0]), value[1] == null ? '' : String(value[1])]
}

/** Operators that always compare calendar days. `is` / `not` count only when the value is a date. */
export const isInventoryV3DateFilterItem = (item: Pick<GridFilterItem, 'operator' | 'value'> | undefined): boolean => {
  if (!item) return false
  if (DATE_COMPARE_OPERATORS.has(item.operator)) return true
  if (item.operator === 'is' || item.operator === 'not') return parseInventoryV3CalendarDay(item.value) !== null
  return false
}

/** True when a date operator has enough input to filter. Incomplete Between stays inactive. */
export const isInventoryV3DateFilterReady = (item: Pick<GridFilterItem, 'operator' | 'value'>): boolean => {
  if (item.operator === 'isEmpty' || item.operator === 'isNotEmpty') return true
  if (item.operator === 'between') {
    const [start, end] = inventoryV3DateRangeValue(item.value)
    return parseInventoryV3CalendarDay(start) !== null && parseInventoryV3CalendarDay(end) !== null
  }
  if (item.operator === 'is' || item.operator === 'not' || DATE_COMPARE_OPERATORS.has(item.operator)) {
    return parseInventoryV3CalendarDay(item.value) !== null
  }
  return false
}

/**
 * Compare a cell to a date filter by the calendar day the user sees.
 * Returns null when `operator` is not a date operator.
 */
export const inventoryV3DateOperatorMatches = (
  cellValue: unknown,
  operator: string,
  filterValue: unknown,
): boolean | null => {
  if (operator === 'is' || operator === 'not') {
    if (parseInventoryV3CalendarDay(filterValue) === null) return null
  } else if (!DATE_COMPARE_OPERATORS.has(operator)) {
    return null
  }
  if (!isInventoryV3DateFilterReady({ operator, value: filterValue })) return false

  const cellDay = parseInventoryV3CalendarDay(cellValue)
  if (cellDay === null) return false

  if (operator === 'between') {
    const [startRaw, endRaw] = inventoryV3DateRangeValue(filterValue)
    const start = parseInventoryV3CalendarDay(startRaw)
    const end = parseInventoryV3CalendarDay(endRaw)
    if (start === null || end === null) return false
    const low = Math.min(start, end)
    const high = Math.max(start, end)
    return cellDay >= low && cellDay <= high
  }

  const filterDay = parseInventoryV3CalendarDay(filterValue)
  if (filterDay === null) return false
  if (operator === 'is') return cellDay === filterDay
  if (operator === 'not') return cellDay !== filterDay
  if (operator === 'before') return cellDay < filterDay
  if (operator === 'after') return cellDay > filterDay
  if (operator === 'onOrAfter') return cellDay >= filterDay
  return cellDay <= filterDay
}
